// Equines: cross-species breeding and pack storage (M19 remainders). PURE (ENG-1).
//
// The grazer archetype breeds like-with-like: two cows make a cow. Horses do not
// work that way, and the exception is the entire point of the family — a horse and
// a DONKEY produce a mule, and the mule is sterile. That is why a mule exists at
// all in Minecraft: it cannot be bred for, only made, and it is the best pack
// animal of the three.
//
// The pack half is the other remainder: a donkey or a mule will carry a chest, and
// that chest is a real container you can open from the saddle. Attaching one is a
// ONE-WAY decision (as in Minecraft) — the chest is part of the animal afterwards,
// and only comes back when the animal dies.
import type { MobDef } from '../registry/schemas';

/** The horse family. Ordered so `equineOffspring` can be a symmetric lookup. */
export const EQUINES = ['horse', 'donkey', 'mule'] as const;
export type Equine = (typeof EQUINES)[number];

export function isEquine(type: string): type is Equine {
  return (EQUINES as readonly string[]).includes(type);
}

/**
 * What a pairing produces, or null when the two will not breed at all.
 *
 *   horse  × horse  → horse
 *   donkey × donkey → donkey
 *   horse  × donkey → MULE   (either way round)
 *   mule   × anything → nothing. A mule is sterile.
 */
export function equineOffspring(a: string, b: string): Equine | null {
  if (!isEquine(a) || !isEquine(b)) return null;
  if (a === 'mule' || b === 'mule') return null;
  if (a === b) return a;
  return 'mule'; // the only remaining pair is horse × donkey
}

/** Can this species be put in love at all? (A mule never can.) */
export function equineCanBreed(type: string): boolean {
  return isEquine(type) && type !== 'mule';
}

/**
 * Will these two seek each other out as mates? Same species for everything else,
 * but a horse and a donkey are a valid pair — which is the only cross-species
 * pairing in the game and the reason `findMate` cannot simply match on type.
 */
export function breedsWith(a: string, b: string): boolean {
  if (isEquine(a) || isEquine(b)) return equineOffspring(a, b) !== null;
  return a === b;
}

// ── Pack storage ─────────────────────────────────────────────────────────────

/** Does this mob carry a chest at all, per its def? */
export function carriesChest(def: MobDef | undefined): boolean {
  return (def?.chestSlots ?? 0) > 0;
}

/** How many slots its chest adds (0 when it carries none). */
export function chestSlotCount(def: MobDef | undefined): number {
  return def?.chestSlots ?? 0;
}

/**
 * Does right-clicking with `held` strap a chest on? Only once, only on a mob that
 * takes one, and never on a baby — a foal has nothing to carry it with.
 */
export function attachesChest(def: MobDef | undefined, hasChest: boolean, baby: boolean, held: string): boolean {
  return carriesChest(def) && !hasChest && !baby && held === 'chest';
}

/** Can this mob's storage be opened right now? */
export function storageOpens(def: MobDef | undefined, hasChest: boolean): boolean {
  return carriesChest(def) && hasChest;
}

/** What a chested pack animal drops on death, beyond its own loot: the chest back,
 *  plus everything that was in it (the host supplies the contents). */
export function chestDropsOnDeath(hasChest: boolean): readonly string[] {
  return hasChest ? ['chest'] : [];
}

// ── Horse armour ─────────────────────────────────────────────────────────────
// The last equine remainder. A barded horse takes the same mitigation curve a
// player's armour does (game/gear.ts `mitigate`), which is what makes a diamond-
// barded horse worth the diamonds — it survives a fall or a skeleton volley that
// would drop a bare one.

/** Defence points each barding grants, Minecraft's values. */
const HORSE_ARMOR: Readonly<Record<string, number>> = Object.freeze({
  leather_horse_armor: 3,
  iron_horse_armor: 5,
  golden_horse_armor: 7,
  diamond_horse_armor: 11,
});

/** Is this item a horse barding? */
export function isHorseArmor(item: string): boolean {
  return item in HORSE_ARMOR;
}

/** Defence points a barding grants, or 0 if it isn't one. */
export function horseArmorPoints(item: string | null | undefined): number {
  return item ? (HORSE_ARMOR[item] ?? 0) : 0;
}

/**
 * Does right-clicking with `held` bard this animal?
 *
 * Only a HORSE — a donkey or a mule carries a chest instead, and Minecraft will
 * not let one wear both. Never a foal, and never over barding it already wears
 * (take that off with the inventory, not by stacking a second suit on top).
 */
export function bardsWith(type: string, worn: string | null, baby: boolean, held: string): boolean {
  return type === 'horse' && !baby && worn === null && isHorseArmor(held);
}

/** What a barded horse drops on death, beyond its own loot: the barding back. */
export function bardingDropsOnDeath(worn: string | null): readonly string[] {
  return worn ? [worn] : [];
}
